import { FormColumns, FormLabel } from "components/form";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import ClientInput from "components/client/input";
import EmployeeInput from "components/employee/input";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import { PermissionControlledInput } from "permissions/components/input";
import SelectName from "components/select/name";
import React from "react";
import { Switch } from "ui/components/switch";
import { projectSchema } from "./schema";
import { useHotkeys } from "react-hotkeys-hook";
import { useProjectForm } from "@/modules/work/projects/project/crud/form/useProjectForm";
import FileInput from "components/file-upload/input";
import { FormActions } from "components/form/actions";
import { StatusTabInput } from "components/status/tabs/input";


import ProjectTagsCombobox from "components/directus/combobox/project-tags";

const ProjectForm = ({ projectId }) => {
  const {
    form,
    requiredFields,
    canReadClient,
    handleCreate,
    handleRead,
    handleUpdate,
  } = useProjectForm({ projectId });

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={projectId || "+"}
      schema={projectSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(false);
              createItem().then(handleClose);
            }
          },
          { preventDefault: true },
        );

        useHotkeys(
          "ctrl+shift+s, cmd+shift+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(true);
              createItem();
            }
          },
          { preventDefault: true },
        );
        return (
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => {
                return (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                      collection="projects"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Status
                      </FormLabel>

                      <StatusTabInput
                        {...field}
                        collection="projects"
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="logo"
              render={({ field }) => {
                return (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                      collection="projects"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Upload Logo
                      </FormLabel>

                      <FileInput
                        className="size-44"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => {
                return (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                      collection="projects"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Name
                      </FormLabel>

                      <Input {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:none"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Project ID
                    </FormLabel>

                    <Input readOnly={true} disabled={!!projectId} {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {canReadClient && (
              <FormField
                control={form.control}
                name="client"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <PermissionControlledInput
                        rule={isNewItem ? "create:*" : "update:*"}
                        collection="projects"
                        type="form"
                        field={field.name}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Client
                        </FormLabel>
                        <ClientInput {...field} />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}

            {/* Coordinators */}
            <FormField
              control={form.control}
              name="sales_coordinator"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Sales Coordinator
                    </FormLabel>
                    <EmployeeInput set="sales_coordinating" {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="account_manager"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Account Manager
                    </FormLabel>
                    <EmployeeInput set="account_manager" {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coordinator"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Project Coordinator
                    </FormLabel>
                    <EmployeeInput set="projects_coordinating" {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="legal_entity"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Entity
                    </FormLabel>
                    <SelectName
                      collection="legal_entities"
                      fetchItems={true}
                      onValueChange={(val) => field.onChange(val)}
                      {...field}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Project Tags */}
            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Tags
                    </FormLabel>
                    <ProjectTagsCombobox
                      isMulti
                      hideLabel
                      values={field.value}
                      onValuesChange={field.onChange}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormActions
              rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
              collection="projects"
              isNewItem={isNewItem}
              isCreating={isCreating}
              createItem={createItem}
              isDirty={isDirty}
              updateItem={updateItem}
              isUpdating={isUpdating}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default ProjectForm;
